import { useEffect, useState } from 'react';
import { Box, Button, TextField, Typography, Link as MuiLink, Avatar, RadioGroup, Radio, FormControlLabel, FormLabel, FormControl, CircularProgress, Alert } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { clearErrors, registerUser } from '../../actions/userAction';
import MetaData from '../Layouts/MetaData';
import AuthLayout from './AuthLayout';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

const Register = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { loading, isAuthenticated, error } = useSelector((state) => state.user);
    const [user, setUser] = useState({
        name: "",
        email: "",
        gender: "",
        password: "",
        cpassword: "",
        mobile: "",
    });
    const { name, email, gender, password, cpassword, mobile } = user;
    const [avatar, setAvatar] = useState();
    const [avatarPreview, setAvatarPreview] = useState("preview.png");

    const handleRegister = (e) => {
        e.preventDefault();
        if (password.length < 8) {
            return;
        }
        if (password !== cpassword) {
            return;
        }
        const formData = new FormData();
        formData.set("name", name);
        formData.set("email", email);
        formData.set("gender", gender);
        formData.set("password", password);
        formData.set("mobile", mobile);

        if (avatar) {
            formData.set("avatar", avatar);
        }

        dispatch(registerUser(formData));
    };

    const handleDataChange = (e) => {
        if (e.target.name === "avatar") {
            const reader = new FileReader();

            reader.onload = () => {
                if (reader.readyState === 2) {
                    setAvatarPreview(reader.result);
                    setAvatar(reader.result);
                }
            };

            reader.readAsDataURL(e.target.files[0]);

        } else {
            setUser({ ...user, [e.target.name]: e.target.value });
        }
    };

    useEffect(() => {
        if (error) {
            console.error(error);
            dispatch(clearErrors());
        }
        if (isAuthenticated) {
            navigate('/');
        }
    }, [dispatch, error, isAuthenticated, navigate]);

    return (
        <AuthLayout
            sidebarTitle="Create Account"
            sidebarTagline="Join us and start your shopping journey!"
        >
            <MetaData title="Register - AmritLok" />
            <Typography component="h2" variant="h5" sx={{ mb: 3, fontWeight: 600, textAlign: 'center' }}>
                Get Started
            </Typography>
            <Box component="form" onSubmit={handleRegister} noValidate sx={{ mt: 1 }}>
                {error && <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{error}</Alert>}
                <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="name"
                    label="Full Name"
                    name="name"
                    value={name}
                    onChange={handleDataChange}
                />
                <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="email"
                    label="Email Address"
                    name="email"
                    autoComplete="email"
                    value={email}
                    onChange={handleDataChange}
                />
                <PhoneInput
                    country={'in'}
                    value={mobile}
                    onChange={(phone) => setUser({ ...user, mobile: phone })}
                    inputStyle={{ 
                        width: '100%',
                        height: '56px',
                        fontSize: '16px',
                        paddingLeft: '58px',
                        borderRadius: '4px',
                        border: '1px solid rgba(0, 0, 0, 0.23)',
                        background: 'transparent',
                    }}
                    containerStyle={{ margin: '16px 0 8px' }}
                    buttonStyle={{ 
                        border: '1px solid rgba(0, 0, 0, 0.23)',
                        borderRadius: '4px 0 0 4px',
                        background: 'transparent',
                    }}
                />

                <FormControl component="fieldset" sx={{ mt: 2, width: '100%' }}>
                    <FormLabel component="legend" sx={{ textAlign: 'left', width: '100%', mb: 1 }}>Gender</FormLabel>
                    <RadioGroup
                        row
                        aria-label="gender"
                        name="gender"
                        value={gender}
                        onChange={handleDataChange}
                    >
                        <FormControlLabel value="male" control={<Radio />} label="Male" />
                        <FormControlLabel value="female" control={<Radio />} label="Female" />
                    </RadioGroup>
                </FormControl>

                <TextField
                    margin="normal"
                    required
                    fullWidth
                    name="password"
                    label="Password"
                    type="password"
                    id="password"
                    autoComplete="new-password"
                    value={password}
                    onChange={handleDataChange}
                    inputProps={{ minLength: 8 }}
                    helperText={password.length < 8 ? "Password must be at least 8 characters" : ""}
                    error={password.length < 8}
                />
                <TextField
                    margin="normal"
                    required
                    fullWidth
                    name="cpassword"
                    label="Confirm Password"
                    type="password"
                    id="confirmPassword"
                    autoComplete="new-password"
                    value={cpassword}
                    onChange={handleDataChange}
                    error={cpassword !== password}
                    helperText={cpassword !== password ? "Passwords do not match" : ""}
                />
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mt: 2,
                        mb: 2,
                    }}
                >
                    <Avatar
                        src={avatarPreview === "preview.png" ? "" : avatarPreview}
                        sx={{ width: 56, height: 56, mr: 2, transition: 'transform 0.2s ease-in-out', '&:hover': { transform: 'scale(1.1)' } }}
                    />
                    <Button
                        component="label"
                        role={undefined}
                        variant="contained"
                        tabIndex={-1}
                        sx={{ transition: 'transform 0.2s ease-in-out', '&:hover': { transform: 'scale(1.05)' } }}
                    >
                        Upload Avatar
                        <VisuallyHiddenInput type="file" onChange={handleDataChange} name="avatar" accept="image/*" />
                    </Button>
                </Box>
                <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    sx={{ mt: 3, mb: 2, py: 1.5, transition: 'transform 0.2s ease-in-out', '&:hover': { transform: 'scale(1.05)' } }}
                    disabled={loading || password.length < 8 || cpassword !== password}
                >
                    {loading ? <CircularProgress size={24} /> : 'Sign Up'}
                </Button>
                <MuiLink component={Link} to="/login" variant="body2" sx={{ display: 'block', textAlign: 'center' }}>
                    Already have an account? Sign In
                </MuiLink>
            </Box>
        </AuthLayout>
    );
};

export default Register;
